import React, { FC, useEffect, useState } from 'react';
import { formatDate, getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { Box } from '@mui/material';
import { DatePicker, LocalizationProvider, PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { isAfter, isBefore, isSameDay, isValid, startOfDay } from 'date-fns';
import { styled } from '@mui/material/styles';
import { disableOrderDeliveryDates } from 'utils/disable-order-delivery-dates';
import TimeMinute from 'components/orders/shared/time-minute.component';
import { Organization } from 'models/organization';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type DeliveryDatePickerProps = {
    onChange?: (date: Date | null, hour: number | undefined, minute: number | undefined, isDateChange: boolean) => void;
    date: Date | null;
    calculatedDate?: Date;
    hour?: number;
    minute?: number;
    lab?: Organization;
    dataTest?: string;
    // The date can be typed as well as picked. A typed date that is incomplete or cannot
    // be picked (closed day, in the past) reaches onChange as null.
    allowTyping?: boolean;
};

// custom styled day component
const WarningDay = styled(PickersDay<Date>)(({ theme }) => ({
    '&.warning': {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.warning.main,
        },
    },
}));

const DeliveryDatePicker: FC<DeliveryDatePickerProps> = ({
    onChange,
    date,
    calculatedDate,
    hour,
    minute,
    lab,
    dataTest,
    allowTyping = false,
}) => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [selectedHour, setSelectedHour] = useState<number | undefined>();
    const [selectedMinute, setSelectedMinute] = useState<number | undefined>();
    const currentOrg = currentOrgVar();
    const minDate = currentOrg?._id !== lab?._id ? new Date() : undefined;

    useEffect(() => {
        setSelectedDate(date);
        setSelectedHour(hour);
        setSelectedMinute(minute);
    }, [date, hour, minute]);

    const disableDates = (date: Date) => {
        if (lab && currentOrg?._id !== lab._id) {
            return disableOrderDeliveryDates(date, lab);
        }
        return false;
    };

    const isSelectableDate = (date: Date) =>
        isValid(date) && !disableDates(date) && !(minDate && isBefore(date, startOfDay(minDate)));

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
        if (allowTyping) {
            onChange?.(date && isSelectableDate(date) ? date : null, selectedHour, selectedMinute, true);
            return;
        }
        if (date && onChange) {
            onChange(date, selectedHour, selectedMinute, true);
        }
    };

    const onTimeChange = (hour: number | undefined, minute: number | undefined) => {
        setSelectedHour(hour);
        setSelectedMinute(minute);
        if (selectedDate && onChange) {
            onChange(selectedDate, hour, minute, false);
        }
    };

    // For new order highlight days between today and calculated date
    const CustomDay = (props: PickersDayProps<Date>) => {
        const { day, outsideCurrentMonth, ...other } = props;
        const today = new Date();
        const isDisabled = disableDates?.(day) ?? false;

        const isBetween =
            calculatedDate && isAfter(day, today) && (isBefore(day, calculatedDate) || isSameDay(day, calculatedDate));

        const isWarning = !isDisabled && isBetween;

        return (
            <WarningDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                className={isWarning ? 'warning' : ''}
            />
        );
    };

    return (
        <Box display="flex">
            <Box mr={1} flexGrow={1}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                    <DatePicker
                        format={getDatePickerFormat()}
                        minDate={minDate}
                        shouldDisableDate={disableDates}
                        value={selectedDate}
                        onChange={handleDateChange}
                        slots={{
                            day: calculatedDate ? CustomDay : undefined, // custom renderer only for new order
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                margin: 'dense',
                                inputProps: {
                                    readOnly: !allowTyping,
                                    'data-test': dataTest,
                                },
                                helperText:
                                    calculatedDate &&
                                    selectedDate &&
                                    calculatedDate.toDateString() !== selectedDate.toDateString()
                                        ? `Outside of delivery Date: ${formatDate(calculatedDate, 'date')}`
                                        : '',
                                FormHelperTextProps: {
                                    sx: {
                                        color:
                                            calculatedDate &&
                                            selectedDate &&
                                            calculatedDate.toDateString() !== selectedDate.toDateString()
                                                ? 'warning.main'
                                                : 'text.secondary',
                                        fontWeight: 'bold',
                                    },
                                },
                            },
                        }}
                    />
                </LocalizationProvider>
            </Box>
            <Box>
                <TimeMinute
                    onTimeChange={onTimeChange}
                    hour={selectedHour}
                    minute={selectedMinute}
                    showFullHourMinute={true}
                    dataTest={`${dataTest}-time-minute`}
                />
            </Box>
        </Box>
    );
};

export default DeliveryDatePicker;
